<template>
  <Transition name="modal-fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm overflow-y-auto"
      @click.self="close"
    >
      <div
        class="bg-white dark:bg-slate-900 w-full max-w-lg rounded-3xl shadow-2xl border border-slate-200/90 dark:border-slate-800 overflow-hidden transform transition-all duration-200 relative my-8"
        role="dialog"
        aria-modal="true"
      >
        <!-- Top Accent Gradient Bar -->
        <div class="h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600"></div>

        <!-- Header -->
        <div class="px-6 pt-5 pb-4 border-b border-slate-100 dark:border-slate-800 flex items-start justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center text-xl shrink-0">
              ⭐
            </div>
            <div>
              <h3 class="text-base sm:text-lg font-black text-slate-900 dark:text-white leading-tight">
                Rate Your Experience
              </h3>
              <div class="flex items-center gap-2 mt-0.5">
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Order #ESK-{{ order?.id }}
                </span>
                <span class="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Verified Purchase
                </span>
              </div>
            </div>
          </div>

          <button
            @click="close"
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Close"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          
          <!-- Multiple Products Notice & Apply to All Toggle -->
          <div
            v-if="products.length > 1"
            class="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 space-y-3"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="text-base">🛒</span>
                <span class="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">
                  {{ products.length }} Products in this Cart
                </span>
              </div>
              <label class="relative inline-flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  v-model="applyToAll"
                  class="sr-only peer"
                />
                <div class="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-amber-500"></div>
              </label>
            </div>

            <p class="text-xs text-slate-600 dark:text-slate-300">
              <strong class="font-bold text-amber-600 dark:text-amber-400">
                {{ applyToAll ? 'One-Click Rating:' : 'Individual Rating:' }}
              </strong>
              {{ applyToAll ? 'Your rating and review below will be applied to all items in this cart.' : 'You can rate each item separately below.' }}
            </p>

            <!-- Product thumbnails preview -->
            <div class="flex flex-wrap gap-2 pt-1">
              <div
                v-for="(p, idx) in products"
                :key="p.subId || idx"
                class="flex items-center gap-2 px-2.5 py-1 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200"
              >
                <img
                  :src="p.imageUrl"
                  alt=""
                  class="w-5 h-5 rounded-md object-cover bg-slate-100"
                  @error="onImgError"
                />
                <span class="truncate max-w-[140px]">{{ p.title }}</span>
              </div>
            </div>
          </div>

          <!-- Single Product Preview (or unified preview when applyToAll is true) -->
          <div
            v-if="products.length === 1 || applyToAll"
            class="space-y-4"
          >
            <!-- Single item header card -->
            <div v-if="products.length === 1" class="flex items-center gap-3.5 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
              <img
                :src="products[0].imageUrl"
                alt=""
                class="w-12 h-12 rounded-xl object-cover bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-700 shadow-sm shrink-0"
                @error="onImgError"
              />
              <div class="min-w-0 flex-1">
                <h4 class="text-sm font-extrabold text-slate-900 dark:text-white truncate">
                  {{ products[0].title }}
                </h4>
                <div class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Qty: {{ products[0].quantity }} &bull; Digital Delivery
                </div>
              </div>
            </div>

            <!-- Stars Selector -->
            <div class="space-y-2">
              <label class="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Your Rating <span class="text-rose-500">*</span>
              </label>
              
              <div class="flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700/80">
                <div class="flex items-center gap-1 select-none">
                  <button
                    v-for="star in 5"
                    :key="star"
                    type="button"
                    @mouseenter="hoverRating = star"
                    @mouseleave="hoverRating = 0"
                    @click="globalRating = star"
                    class="p-1 transition-transform hover:scale-125 active:scale-95 text-2xl focus:outline-none"
                    :title="`${star} of 5 stars`"
                  >
                    <span :class="star <= (hoverRating || globalRating) ? 'text-amber-400 drop-shadow-sm' : 'text-slate-300 dark:text-slate-700'">
                      ★
                    </span>
                  </button>
                </div>

                <div class="text-sm font-extrabold text-amber-500 sm:border-l sm:border-slate-200 sm:dark:border-slate-700 sm:pl-3">
                  {{ ratingLabels[hoverRating || globalRating] || 'Tap stars to rate' }}
                </div>
              </div>
            </div>

            <!-- Comment Input -->
            <div class="space-y-1.5">
              <label class="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Written Review <span class="text-slate-400 font-normal lowercase">(optional)</span>
              </label>
              <textarea
                v-model="globalComment"
                rows="3"
                maxlength="1000"
                placeholder="What did you like about this product? (Instant delivery, code worked perfectly, friendly support...)"
                class="w-full px-4 py-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all placeholder:text-slate-400"
              ></textarea>
              <div class="flex justify-between items-center text-[11px] text-slate-400 px-1">
                <span>Helps other gamers and verified buyers</span>
                <span>{{ globalComment.length }} / 1000</span>
              </div>
            </div>
          </div>

          <!-- Individual Items (When applyToAll is false) -->
          <div
            v-else
            class="space-y-4"
          >
            <div
              v-for="(p, pIdx) in products"
              :key="p.subId || pIdx"
              class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3"
            >
              <div class="flex items-center gap-3">
                <img
                  :src="p.imageUrl"
                  alt=""
                  class="w-10 h-10 rounded-xl object-cover bg-white shrink-0 border border-slate-200"
                  @error="onImgError"
                />
                <div class="min-w-0 flex-1">
                  <h4 class="text-xs font-extrabold text-slate-900 dark:text-white truncate">
                    {{ p.title }}
                  </h4>
                  <div class="flex items-center gap-1 mt-1">
                    <button
                      v-for="star in 5"
                      :key="star"
                      type="button"
                      @click="p.rating = star"
                      class="text-lg leading-none focus:outline-none transition-transform hover:scale-110"
                    >
                      <span :class="star <= p.rating ? 'text-amber-400' : 'text-slate-300 dark:text-slate-700'">
                        ★
                      </span>
                    </button>
                    <span class="text-[11px] font-bold text-amber-500 ml-1">
                      {{ ratingLabels[p.rating] }}
                    </span>
                  </div>
                </div>
              </div>

              <input
                type="text"
                v-model="p.comment"
                placeholder="Comment for this item (optional)..."
                maxlength="500"
                class="w-full px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder:text-slate-400"
              />
            </div>
          </div>

          <!-- Anonymous Toggle -->
          <div class="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
            <div class="flex items-center gap-3">
              <span class="text-xl">🎭</span>
              <div>
                <div class="text-xs font-bold text-slate-800 dark:text-slate-200">
                  Post anonymously
                </div>
                <div class="text-[11px] text-slate-400">
                  Hide your full name on the public website review list
                </div>
              </div>
            </div>
            <label class="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                v-model="isAnonymous"
                class="sr-only peer"
              />
              <div class="w-9 h-5 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-amber-500"></div>
            </label>
          </div>

          <!-- Error Alert if any -->
          <div
            v-if="errorMessage"
            class="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-xs font-bold text-rose-600 dark:text-rose-400 flex items-center gap-2"
          >
            <span>⚠️</span>
            <span>{{ errorMessage }}</span>
          </div>

        </div>

        <!-- Footer Actions -->
        <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-3">
          <button
            type="button"
            @click="close"
            :disabled="isSubmitting"
            class="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            @click="submitRatings"
            :disabled="isSubmitting || (products.length === 1 && globalRating < 1)"
            class="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 active:scale-95"
          >
            <span
              v-if="isSubmitting"
              class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"
            ></span>
            <span>{{ isSubmitting ? 'Submitting...' : 'Submit Rating' }}</span>
          </button>
        </div>

      </div>
    </div>
  </Transition>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { ratingService } from '@/services/api';
import { useDataStore } from '@/stores/dataStore';
import Swal from 'sweetalert2';

export default {
  name: 'RatingModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      default: null
    },
    cartOrders: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'submitted'],
  setup(props, { emit }) {
    const dataStore = useDataStore();

    const globalRating = ref(5);
    const hoverRating = ref(0);
    const globalComment = ref('');
    const isAnonymous = ref(false);
    const applyToAll = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');

    const ratingLabels = {
      1: '⭐ Poor',
      2: '⭐⭐ Fair',
      3: '⭐⭐⭐ Good',
      4: '⭐⭐⭐⭐ Very Good',
      5: '⭐⭐⭐⭐⭐ Excellent!'
    };

    // Helper to resolve product image and title for an order item
    const resolveItem = (ord) => {
      let subId = ord.sous_categorie_id;
      if (!subId && ord.article_id && dataStore.articles) {
        const art = dataStore.articles.find(a => a.id === ord.article_id);
        if (art && art.sous_categorie_id) {
          subId = art.sous_categorie_id;
        }
      }

      const subcat = dataStore.subcategories ? dataStore.subcategories.find(s => s.id === subId) : null;
      const title = ord.article_title || ord.article_libelle || subcat?.libelle || 'Digital Product';

      let imageUrl = '/placeholder-product.png';
      if (subcat?.image) {
        if (subcat.image.startsWith('http')) {
          imageUrl = subcat.image;
        } else {
          const base = (import.meta.env.VITE_IMAGE_URL || 'https://api.eskrag.com').replace(/\/+$/, '');
          const clean = subcat.image.replace(/^\/+/, '');
          if (clean.includes('assests/')) {
            imageUrl = `${base}/${clean}`;
          } else {
            imageUrl = `${base}/assests/Souscategorie/${clean}`;
          }
        }
      }

      return {
        orderId: ord.id,
        subId: subId || ord.id,
        title,
        imageUrl,
        quantity: ord.quantity || 1,
        rating: 5,
        comment: ''
      };
    };

    // Reactive products list
    const products = ref([]);

    // Populate products whenever modal opens or order changes
    watch(
      () => [props.isOpen, props.order, props.cartOrders],
      ([isOpen, order, cartOrders]) => {
        if (isOpen && order) {
          errorMessage.value = '';
          globalRating.value = 5;
          hoverRating.value = 0;
          globalComment.value = '';
          isAnonymous.value = false;
          applyToAll.value = true;

          // If cart orders were passed and has multiple orders
          if (Array.isArray(cartOrders) && cartOrders.length > 1) {
            // Deduplicate by subId so we don't ask to rate the exact same subcategory twice
            const seen = new Set();
            const list = [];
            for (const ord of cartOrders) {
              const item = resolveItem(ord);
              if (!seen.has(item.subId)) {
                seen.add(item.subId);
                list.push(item);
              }
            }
            products.value = list.length > 0 ? list : [resolveItem(order)];
          } else {
            products.value = [resolveItem(order)];
          }
        }
      },
      { immediate: true }
    );

    const onImgError = (e) => {
      e.target.src = '/placeholder-product.png';
    };

    const close = () => {
      if (isSubmitting.value) return;
      emit('close');
    };

    const submitRatings = async () => {
      errorMessage.value = '';
      isSubmitting.value = true;

      try {
        const promises = [];

        if (products.value.length === 1 || applyToAll.value) {
          // Apply global rating & comment to all products
          for (const p of products.value) {
            promises.push(
              ratingService.submitRating('subcategory', p.subId, {
                rating: globalRating.value,
                comment: globalComment.value ? globalComment.value.trim() : null,
                is_anonymous: isAnonymous.value
              })
            );
          }
        } else {
          // Individual ratings
          for (const p of products.value) {
            promises.push(
              ratingService.submitRating('subcategory', p.subId, {
                rating: p.rating || 5,
                comment: p.comment ? p.comment.trim() : null,
                is_anonymous: isAnonymous.value
              })
            );
          }
        }

        await Promise.all(promises);

        // Success Feedback
        Swal.fire({
          icon: 'success',
          title: 'Thank You! ⭐',
          text: 'Your verified rating has been submitted successfully!',
          timer: 2500,
          showConfirmButton: false,
          customClass: {
            popup: 'rounded-3xl'
          }
        });

        // Emit submitted with order and rating info
        emit('submitted', {
          orderId: props.order.id,
          rating: globalRating.value,
          productIds: products.value.map(p => p.subId)
        });

        emit('close');
      } catch (err) {
        console.error('Rating submission failed:', err);
        errorMessage.value = err.response?.data?.message || 'Failed to submit rating. Please try again.';
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      products,
      globalRating,
      hoverRating,
      globalComment,
      isAnonymous,
      applyToAll,
      isSubmitting,
      errorMessage,
      ratingLabels,
      onImgError,
      close,
      submitRatings
    };
  }
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
